import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { servicesData } from '../../data/services';
import Contact from '../Contact';
import { useEffect } from 'react';
import { 
  ChevronRightIcon, 
  CheckCircleIcon, 
  QuestionMarkCircleIcon,
  ArrowsPointingOutIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';
import SEO from '../SEO';

// Import all service images
import appointmentAiImage from '../../assets/appointmentai.png';
import customAiImage from '../../assets/customai.png';
import ragBotsImage from '../../assets/RAGai.png';
import webAppAiImage from '../../assets/webappai.png';
import processAiImage from '../../assets/processai.png';
import dataScienceAiImage from '../../assets/datascienceai.png';

// Import Scheduli AI showcase image
import demoImg from '../../assets/demo.png';

// Import GolfAI Guru showcase image
import golfDemoImg from '../../assets/golf-demo.png';

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = servicesData.find((s) => s.slug === slug);

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, [slug, service]);

  if (!service) {
    return (
      <div className="mt-24 container px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-24 text-center">
        <SEO 
          title="Service Not Found | BetterByAI"
          description="The service you're looking for doesn't exist or may have been moved."
        />
        <h1 className="text-3xl md:text-4xl font-bold mb-6 text-primary">Service Not Found</h1>
        <p className="text-lg text-secondary mb-8">The service you're looking for doesn't exist or may have been moved.</p>
        <Link to="/services" className="inline-flex items-center bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary/90 transition-colors">
          View All Services
          <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
          </svg>
        </Link>
      </div>
    );
  }

  // Get service-specific image
  const getServiceImage = () => {
    switch(service.slug) {
      case 'ai-appointment-setters':
        return appointmentAiImage;
      case 'custom-ai-solutions':
        return customAiImage;
      case 'rag-bots':
        return ragBotsImage;
      case 'ai-web-applications':
        return webAppAiImage;
      case 'process-automation':
        return processAiImage;
      case 'data-science-solutions':
        return dataScienceAiImage;
      default:
        return null;
    }
  };

  // Use service-specific data with fallbacks
  const features = service.features || [
    "Enterprise-grade solutions with robust security measures",
    "Customisable to fit your specific business workflows",
    "Ongoing support and implementation assistance",
    "Seamless integration with existing operational systems"
  ];

  const benefits = service.benefits || [
    "Increased operational efficiency across departments",
    "Significant cost reduction in day-to-day operations",
    "Enhanced data-driven decision making capabilities", 
    "Improved customer experiences and satisfaction rates"
  ];

  const useCases = service.useCases || [
    {
      title: "Enterprise Implementation",
      description: "Streamline operations across departments with integrated AI solutions tailored for large organisations."
    },
    {
      title: "Small Business Solutions",
      description: "Affordable AI tools that help small businesses compete with larger enterprises in today's digital marketplace."
    },
    {
      title: "Customer Service Enhancement",
      description: "Improve response times and customer satisfaction with intelligent automation for support teams."
    }
  ];

  // Use service-specific FAQs with fallbacks
  const faqs = service.faqs || [
    {
      question: `How does ${service.title} integrate with existing systems?`,
      answer: `Our ${service.title} solutions are designed for seamless integration with your current tech stack and business processes. We utilise industry-standard APIs and custom connectors to ensure minimal disruption during implementation whilst maximising the benefits of AI technology for your organisation.`
    },
    {
      question: `What kind of ROI can we expect from implementing ${service.title}?`,
      answer: `Most clients implementing our ${service.title} solution report significant returns within 3-6 months. This typically includes 30-60% efficiency improvements, 15-25% cost reductions, and measurable improvements in customer satisfaction. We provide detailed analytics dashboards that track your specific KPIs to demonstrate concrete value.`
    },
    {
      question: `What makes BetterByAI's approach to ${service.title.toLowerCase()} different?`,
      answer: `Unlike cookie-cutter solutions, our ${service.title} offerings are custom-tailored for your specific business needs. We combine cutting-edge AI technology with human expertise to create systems that truly understand your unique challenges. Our consultative approach and ongoing optimisation ensure that the solutions grow with your business.`
    },
    {
      question: `How long does implementation typically take?`,
      answer: `While timelines vary based on complexity, most of our ${service.title} implementations take 4-8 weeks from initial consultation to full deployment. We provide comprehensive project management throughout to ensure timely delivery and minimal business disruption.`
    }
  ];

  // Use service-specific implementation steps with fallbacks
  const implementationSteps = service.implementationSteps || [
    {
      number: "01",
      title: "Discovery Consultation",
      description: "We begin with a thorough needs assessment to understand your unique business challenges and objectives."
    },
    {
      number: "02",
      title: "Solution Design",
      description: "Our experts create a tailored implementation plan specifically for your organisational requirements."
    },
    {
      number: "03",
      title: "Development & Integration",
      description: "We build and integrate the solution with your existing systems, ensuring seamless data flow."
    },
    {
      number: "04",
      title: "Testing & Refinement",
      description: "Rigorous testing ensures the solution performs optimally under real-world conditions."
    },
    {
      number: "05",
      title: "Deployment & Training",
      description: "We deploy the solution and provide comprehensive training for your team to ensure adoption."
    },
    {
      number: "06",
      title: "Ongoing Support",
      description: "Our team provides continuous support and optimisation to maximise your ROI over time."
    }
  ];
  
  // Use service-specific industry stats with fallbacks
  const industryStats = service.industryStats || [
    {
      value: "67%",
      description: "of businesses report improved decision making after implementing AI solutions"
    },
    {
      value: "£4.8M",
      description: "average annual savings for enterprise companies using advanced AI automation"
    },
    {
      value: "42%",
      description: "increase in operational efficiency reported by our clients within 6 months"
    },
    {
      value: "3-5x",
      description: "return on investment within the first year of implementation"
    }
  ];

  // Scheduli AI Features
  const scheduliFeatures = [
    {
      icon: DocumentTextIcon,
      title: "Intelligent Scheduling",
      description: "Automatically optimises schedules based on priorities, availability, and resource constraints."
    },
    {
      icon: ShieldCheckIcon,
      title: "Secure Data Handling",
      description: "Ensures all scheduling data is encrypted and compliant with GDPR standards."
    },
    {
      icon: UserGroupIcon,
      title: "Team Collaboration",
      description: "Facilitates seamless collaboration across teams with shared schedules and notifications."
    },
    {
      icon: ArrowsPointingOutIcon,
      title: "Scalable Architecture",
      description: "Designed to handle growing business needs without compromising performance."
    }
  ];

  // Scheduli Feature Component
  const ScheduliFeature = ({ icon: Icon, title, description }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border border-primary/10"
    >
      <div className="flex items-start gap-3">
        <div className="mt-1 p-2 rounded-full bg-primary/10">
          <Icon className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-primary mb-2">{title}</h3>
          <p className="text-secondary">{description}</p>
        </div>
      </div>
    </motion.div>
  );

  // Generate service-specific FAQ schema
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
  
  // Generate service schema
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": service.title,
    "description": service.description,
    "provider": {
      "@type": "Organization",
      "name": "BetterByAI",
      "url": "https://betterbyai.com"
    },
    "url": `https://betterbyai.com/services/${service.slug}`
  };

  return (
    <div className="bg-gradient-to-b from-white via-[#FF7066]/5 to-white overflow-hidden">
      {/* SEO Component */}
      <SEO 
        title={`${service.title} | BetterByAI - Leading AI Solutions`}
        description={service.description}
        keywords={`${service.title}, AI solutions, custom AI, business automation, ${service.category || 'enterprise solutions'}, generative ai, ${service.slug.replace(/-/g, ' ')}`}
        url={`https://betterbyai.com/services/${service.slug}`}
        type="article"
        canonical={`https://betterbyai.com/services/${service.slug}`}
        schemaMarkup={[serviceSchema, faqSchema]}
      />
      
      {/* Hero Section */}
      <section className="mt-24 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 bg-grid-pattern opacity-[0.03]" />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(circle at 50% 50%, rgba(255, 112, 102, 0.03), transparent 70%)',
          }}
        />
        
        {/* Floating Orbs */}
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            repeatType: "reverse",
          }}
          className="absolute top-20 right-20 w-64 h-64 bg-[#FF7066]/10 rounded-full blur-3xl"
        />
        
        <div className="container relative z-10 px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="max-w-7xl mx-auto">
            {/* Breadcrumb - Good for SEO */}
            <nav className="mb-8" aria-label="Breadcrumb">
              <ol className="flex items-center space-x-2 text-sm">
                <li>
                  <Link to="/" className="text-gray-500 hover:text-[#FF7066] transition-colors">Home</Link>
                </li>
                <ChevronRightIcon className="w-4 h-4 text-gray-400" />
                <li>
                  <Link to="/services" className="text-gray-500 hover:text-[#FF7066] transition-colors">Services</Link>
                </li>
                <ChevronRightIcon className="w-4 h-4 text-gray-400" />
                <li className="text-[#FF7066] font-medium">{service.title}</li>
              </ol>
            </nav>

            <div className="flex flex-col lg:flex-row items-center gap-12">
              {/* Content */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="lg:w-1/2"
              >
                <div className="flex items-center gap-2 mb-4">
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-[#FF7066]/10 text-[#FF7066] text-xs font-medium">
                    {service.category || "Enterprise Solution"}
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-secondary/10 text-secondary text-xs font-medium">
                    AI-Powered
                  </span>
                </div>
                
                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary mb-6"
                >
                  {service.title}
                </motion.h1>
                
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className="text-xl text-secondary mb-8 leading-relaxed"
                >
                  {service.description}
                </motion.p>
                
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                  className="flex flex-wrap gap-4"
                >
                  <a 
                    href="https://calendly.com/amrit-betterbyai/30min" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center bg-[#FF7066] text-white px-8 py-4 rounded-lg hover:bg-[#FF8A82] transition-colors shadow-lg shadow-[#FF7066]/20"
                  >
                    Book a Consultation
                    <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                    </svg>
                  </a>
                  <a 
                    href="mailto:contact@betterbyai.com" 
                    className="inline-flex items-center border-2 border-[#FF7066] text-[#FF7066] px-8 py-4 rounded-lg hover:bg-[#FF7066]/10 transition-colors"
                  >
                    Request Information
                  </a>
                </motion.div>
              </motion.div>
              
              {/* Image */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="lg:w-1/2"
              >
                <div className="relative">
                  <div className="absolute -inset-4 bg-gradient-to-r from-[#FF7066]/20 to-secondary/20 rounded-2xl blur-lg"></div>
                  <div className="relative aspect-video bg-white rounded-xl overflow-hidden shadow-xl flex items-center justify-center group">
                    {getServiceImage() ? (
                      <div className="relative w-full h-full flex items-center justify-center">
                        <img 
                          src={getServiceImage()} 
                          alt={`${service.title} - BetterByAI's enterprise AI solution`}
                          className="max-w-[80%] max-h-[80%] object-contain z-10 transition-transform duration-500 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-br from-[#FF7066]/5 to-secondary/5 z-0"></div>
                      </div>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#FF7066]/5 to-secondary/5">
                        {service.icon && <service.icon className="w-24 h-24 text-[#FF7066]/30 group-hover:text-[#FF7066]/50 transition-colors duration-300" />}
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition Stats - Visual impact */}
      <section className="py-12 bg-gradient-to-r from-[#FF7066]/5 to-secondary/5">
        <div className="container px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {industryStats.slice(0, 4).map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="text-4xl md:text-5xl font-bold text-[#FF7066] mb-2">{stat.value}</div>
                  <p className="text-secondary">{stat.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Overview Section - Expanded content for SEO */}
      <section className="py-16 bg-white">
        <div className="container px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="lg:w-1/2"
              >
                <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">Transforming Business Through Advanced AI</h2>
                <p className="text-lg text-secondary mb-6 leading-relaxed">
                  Our {service.title} solutions represent the pinnacle of AI innovation, designed specifically to address the unique challenges facing modern businesses today. By harnessing the power of artificial intelligence, machine learning, and advanced data analytics, we deliver solutions that not only optimise current operations but position your organisation for future growth.
                </p>
                <p className="text-lg text-secondary mb-6 leading-relaxed">
                  In today's competitive landscape, businesses that leverage AI technology effectively gain significant advantages in efficiency, cost reduction, and customer satisfaction. Our {service.title} offerings provide a strategic edge by automating complex processes, delivering actionable insights from your data, and creating seamless experiences for both your team and customers.
                </p>
                <div className="flex items-center gap-4 p-4 bg-[#FF7066]/5 rounded-lg">
                  <ShieldCheckIcon className="w-12 h-12 text-[#FF7066]" />
                  <div>
                    <h4 className="font-medium text-[#FF7066]">Enterprise-Grade Security</h4>
                    <p className="text-secondary text-sm">All our solutions feature robust security protocols and GDPR compliance.</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="lg:w-1/2 bg-gradient-to-br from-[#FF7066]/5 to-secondary/5 rounded-2xl p-8"
              >
                <h3 className="text-2xl font-bold text-[#FF7066] mb-6">Why Businesses Choose Our {service.title}</h3>
                <ul className="space-y-4">
                  {service.benefits && service.benefits.slice(0, 8).map((benefit, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircleIcon className="w-6 h-6 text-[#FF7066] flex-shrink-0 mt-0.5" />
                      <span className="text-secondary">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="container px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <motion.span
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="inline-block px-3 py-1 rounded-full bg-[#FF7066]/10 text-[#FF7066] text-sm font-medium mb-4"
              >
                Comprehensive Capabilities
              </motion.span>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="text-3xl md:text-4xl font-bold text-primary mb-4"
              >
                Key Features &amp; Capabilities
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="text-xl text-secondary max-w-3xl mx-auto"
              >
                Our {service.title} solution includes everything your organisation needs for seamless implementation and maximum return on investment. Each feature has been meticulously designed to address specific business challenges.
              </motion.p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {features.map((feature, index) => {
                // Generate dynamic feature titles based on feature content
                const getFeatureTitle = () => {
                  const feature_lower = feature.toLowerCase();
                  
                  if (feature_lower.includes("secur") || feature_lower.includes("gdpr") || feature_lower.includes("complian")) 
                    return "Enterprise-Grade Security";
                  
                  if (feature_lower.includes("integrat") || feature_lower.includes("connect") || feature_lower.includes("existing"))
                    return "Seamless Integration";
                  
                  if (feature_lower.includes("custom") || feature_lower.includes("tailor") || feature_lower.includes("personal"))
                    return "Customisation Options";
                    
                  if (feature_lower.includes("support") || feature_lower.includes("assist") || feature_lower.includes("help"))
                    return "Dedicated Support";
                    
                  if (feature_lower.includes("data") || feature_lower.includes("insight") || feature_lower.includes("analytic"))
                    return "Advanced Analytics";
                    
                  if (feature_lower.includes("automat") || feature_lower.includes("workflow") || feature_lower.includes("process"))
                    return "Process Automation";
                    
                  if (feature_lower.includes("scale") || feature_lower.includes("growth") || feature_lower.includes("expand"))
                    return "Enterprise Scalability";
                    
                  if (feature_lower.includes("train") || feature_lower.includes("learn") || feature_lower.includes("teach"))
                    return "Training & Onboarding";
                    
                  if (feature_lower.includes("interface") || feature_lower.includes("dashboard") || feature_lower.includes("user experience"))
                    return "Intuitive Interface";
                    
                  if (feature_lower.includes("api") || feature_lower.includes("developer") || feature_lower.includes("extensib"))
                    return "Developer Tools";
                  
                  // Default titles based on position if content doesn't match any patterns
                  const defaultTitles = [
                    "Core Functionality", 
                    "Advanced Capabilities", 
                    "Enterprise Integration",
                    "Flexible Configuration",
                    "Performance Optimisation",
                    "Security Framework",
                    "User Management",
                    "Reporting Tools"
                  ];
                  
                  return defaultTitles[index % defaultTitles.length];
                };
                
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-[#FF7066]/5 hover:border-[#FF7066]/20 group"
                  >
                    <div className="flex items-start gap-4">
                      <div className="mt-1 p-2 rounded-full bg-[#FF7066]/10 group-hover:bg-[#FF7066]/20 transition-colors">
                        <svg className="w-6 h-6 text-[#FF7066]" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-[#FF7066] mb-3">{getFeatureTitle()}</h3>
                        <p className="text-lg text-secondary">{feature}</p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* AI Applications Showcase - Only shown for AI Web Applications service */}
      {service.slug === 'ai-web-applications' && (
        <section id="ai-applications" className="py-20 relative overflow-hidden bg-gradient-to-b from-white via-[#FF7066]/5 to-white">
          {/* Background Elements */}
          <div className="absolute inset-0 bg-grid-pattern opacity-[0.03]" />
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 1 }}
            viewport={{ once: true }}
            className="absolute inset-0"
            style={{
              background: 'radial-gradient(circle at 50% 50%, rgba(255, 112, 102, 0.03), transparent 70%)',
            }}
          />
          
          <div className="container relative z-10 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
              {/* Header */}
              <div className="text-center max-w-3xl mx-auto mb-16">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true }}
                  className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-[#FF7066]/10 to-secondary/10 text-[#FF7066] text-sm font-medium border border-[#FF7066]/10 mb-6"
                >
                  Featured Applications
                </motion.div>
                
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  viewport={{ once: true }}
                  className="text-3xl md:text-4xl font-bold text-primary mb-6"
                >
                  Our Latest <span className="bg-gradient-to-r from-[#FF7066] to-secondary bg-clip-text text-transparent">AI Web Applications</span>
                </motion.h2>
                
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  viewport={{ once: true }}
                  className="text-xl text-secondary mb-8"
                >
                  Explore our AI-powered web applications that showcase the capabilities we bring to our clients.
                </motion.p>
              </div>

              {/* Application Cards */}
              <div className="grid md:grid-cols-2 gap-12">
                {/* Scheduli AI */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true }}
                  className="relative"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-secondary/10 to-transparent rounded-3xl" />
                  <div className="relative bg-white/50 backdrop-blur-sm rounded-3xl border border-primary/10 p-8 hover:shadow-xl hover:shadow-primary/5 transition-all duration-300 h-full">
                    <div className="aspect-video bg-primary/5 rounded-2xl mb-6 flex items-center justify-center">
                      <img src={demoImg} alt="Scheduli AI Preview" className="object-cover rounded-xl shadow-lg" />
                    </div>
                    
                    <div className="space-y-4">
                      <h3 className="text-2xl font-bold text-primary">Scheduli AI</h3>
                      <p className="text-secondary">
                        Built with cutting-edge AI technology, Scheduli AI revolutionizes how businesses handle their scheduling needs. Our solution combines powerful LLM capabilities with intuitive design for an unmatched scheduling experience.
                      </p>
                      
                      {/* Feature list */}
                      <div className="grid grid-cols-2 gap-3 my-4">
                        <div className="flex items-center gap-2">
                          <svg className="w-5 h-5 text-[#FF7066]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-sm">AI-Powered Planning</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <svg className="w-5 h-5 text-[#FF7066]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-sm">Smart Optimization</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <svg className="w-5 h-5 text-[#FF7066]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-sm">Seamless Integration</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <svg className="w-5 h-5 text-[#FF7066]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-sm">Time-Saving Automation</span>
                        </div>
                      </div>
                      
                      <a
                        href="https://scheduli-ai.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors duration-300"
                      >
                        Visit Scheduli AI
                        <ArrowTopRightOnSquareIcon className="w-5 h-5" />
                      </a>
                    </div>
                  </div>
                </motion.div>

                {/* GolfAI Guru */}
                <motion.div
                  id="golfai-guru"  
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  viewport={{ once: true }}
                  className="relative"
                >
                  <div className="absolute inset-0 bg-gradient-to-bl from-primary/10 via-secondary/10 to-transparent rounded-3xl" />
                  <div className="relative bg-white/50 backdrop-blur-sm rounded-3xl border border-primary/10 p-8 hover:shadow-xl hover:shadow-primary/5 transition-all duration-300 h-full">
                    <div className="aspect-video bg-primary/5 rounded-2xl mb-6 flex items-center justify-center">
                      <img src={golfDemoImg} alt="GolfAI Guru Preview" className="object-cover rounded-xl shadow-lg" />
                    </div>
                    
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-2xl font-bold text-primary">GolfAI Guru</h3>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          New
                        </span>
                      </div>
                      <p className="text-secondary">
                        GolfAI Guru uses state-of-the-art vision AI to analyze your golf swing from any video. Get instant feedback on your posture, swing plane, timing, and follow-through with personalized tips from our AI coach.
                      </p>
                      
                      {/* Feature list */}
                      <div className="grid grid-cols-2 gap-3 my-4">
                        <div className="flex items-center gap-2">
                          <svg className="w-5 h-5 text-[#FF7066]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-sm">Vision Analysis</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <svg className="w-5 h-5 text-[#FF7066]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-sm">Pose Data Extraction</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <svg className="w-5 h-5 text-[#FF7066]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-sm">Expert LLM Coaching</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <svg className="w-5 h-5 text-[#FF7066]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-sm">Continuous Improvement</span>
                        </div>
                      </div>
                      
                      <a
                        href="https://golfaiguru.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors duration-300"
                      >
                        Try GolfAI Guru
                        <ArrowTopRightOnSquareIcon className="w-5 h-5" />
                      </a>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Bottom CTA */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="text-center mt-12 p-6 bg-gradient-to-r from-primary/5 to-secondary/5 rounded-xl"
              >
                <p className="text-lg text-primary font-medium mb-4">
                  These are just examples of the powerful AI web applications we can build for your business.
                </p>
                <a 
                  href="https://calendly.com/amrit-betterbyai/30min" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center bg-[#FF7066] text-white px-6 py-3 rounded-lg hover:bg-[#FF8A82] transition-colors shadow-lg shadow-[#FF7066]/10"
                >
                  Discuss Your Web Application Idea
                  <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                  </svg>
                </a>
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* Implementation Process - Visual timeline */}
      <section className="py-16 bg-white overflow-hidden">
        <div className="container px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="text-3xl md:text-4xl font-bold text-primary mb-4"
              >
                Implementation Process
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="text-xl text-secondary max-w-3xl mx-auto"
              >
                Our proven methodology ensures successful deployment with minimal disruption to your business operations.
              </motion.p>
            </div>

            <div className="relative">
              {/* Timeline line */}
              <div className="absolute top-0 bottom-0 left-1/2 w-0.5 bg-[#FF7066]/20 transform -translate-x-1/2 hidden md:block"></div>
              
              <div className="space-y-24">
                {implementationSteps.map((step, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className={`relative flex flex-col ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} items-center`}
                  >
                    <div className="md:w-1/2 mb-8 md:mb-0">
                      <div className={`p-6 rounded-xl bg-white shadow-lg border border-[#FF7066]/10 ${index % 2 === 0 ? 'md:mr-8' : 'md:ml-8'} hover:shadow-xl transition-shadow`}>
                        <div className="text-4xl font-bold text-[#FF7066]/20 mb-2">{step.number}</div>
                        <h3 className="text-2xl font-bold text-[#FF7066] mb-3">{step.title}</h3>
                        <p className="text-secondary">{step.description}</p>
                      </div>
                    </div>
                    
                    {/* Timeline marker */}
                    <div className="md:absolute md:left-1/2 md:transform md:-translate-x-1/2 bg-[#FF7066] text-white w-12 h-12 rounded-full flex items-center justify-center shadow-lg z-10">
                      {parseInt(step.number) || index + 1}
                    </div>
                    
                    <div className="md:w-1/2"></div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="container px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <motion.span
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="inline-block px-3 py-1 rounded-full bg-[#FF7066]/10 text-[#FF7066] text-sm font-medium mb-4"
              >
                Real-World Applications
              </motion.span>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="text-3xl md:text-4xl font-bold text-primary mb-4"
              >
                Common Use Cases
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="text-xl text-secondary max-w-3xl mx-auto"
              >
                Discover how organisations like yours are implementing our {service.title} solution to transform their operations and achieve remarkable results.
              </motion.p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {useCases.map((useCase, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-[#FF7066]/10 hover:-translate-y-1"
                >
                  <div className="mb-6 p-3 w-fit rounded-xl bg-[#FF7066]/10">
                    {/* Choose an appropriate icon based on the use case, or use a generic one */}
                    <UserGroupIcon className="w-8 h-8 text-[#FF7066]" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#FF7066] mb-3">{useCase.title}</h3>
                  <p className="text-secondary mb-6">{useCase.description}</p>
                  
                  <a 
                    href="https://calendly.com/amrit-betterbyai/30min"
                    target="_blank"
                    rel="noopener noreferrer" 
                    className="flex items-center text-[#FF7066] font-medium hover:underline"
                  >
                    <span>Book a Consultation</span>
                    <ArrowsPointingOutIcon className="w-4 h-4 ml-2" />
                  </a>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-white">
        <div className="container px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center mb-12"
            >
              <span className="inline-block px-3 py-1 rounded-full bg-[#FF7066]/10 text-[#FF7066] text-sm font-medium mb-4">
                Business Value
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">Key Benefits</h2>
              <p className="text-xl text-secondary max-w-3xl mx-auto">
                Here's how our {service.title} solution will positively impact your business operations and bottom line.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white p-8 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border border-[#FF7066]/10"
                >
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-full bg-[#FF7066]/10 flex-shrink-0">
                      <svg className="w-6 h-6 text-[#FF7066]" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"></path>
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-primary mb-3">{benefit}</h3>
                      <p className="text-secondary">
                        {benefit.toLowerCase().includes("efficiency") ? 
                          "Our clients typically see operational improvements within weeks of implementation, with streamlined workflows and reduced manual tasks." :
                        benefit.toLowerCase().includes("cost") ?
                          "Reduce expenditure through intelligent automation and process optimisation, delivering significant savings to your bottom line." :
                        benefit.toLowerCase().includes("data") ?
                          "Transform raw data into actionable insights that inform strategic decisions and create competitive advantage." :
                          "Create seamless, personalised experiences that increase loyalty and drive higher customer lifetime value."
                        }
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section - Great for SEO */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="container px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center p-1 px-3 rounded-full bg-[#FF7066]/10 mb-4"
              >
                <QuestionMarkCircleIcon className="w-5 h-5 text-[#FF7066] mr-2" />
                <span className="text-[#FF7066] text-sm font-medium">Frequently Asked Questions</span>
              </motion.div>
              
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="text-3xl md:text-4xl font-bold text-primary mb-4"
              >
                Common Questions About {service.title}
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="text-xl text-secondary max-w-3xl mx-auto"
              >
                Find answers to frequently asked questions about our solutions, implementation process, and expected outcomes.
              </motion.p>
            </div>

            <div className="max-w-4xl mx-auto divide-y divide-gray-200">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="py-6"
                >
                  <h3 className="text-xl font-bold text-[#FF7066] mb-3">{faq.question}</h3>
                  <p className="text-secondary">{faq.answer}</p>
                </motion.div>
              ))}
            </div>

            <div className="text-center mt-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.6 }}
              >
                <p className="text-secondary mb-4">Have more questions about {service.title}?</p>
                <a 
                  href="mailto:contact@betterbyai.com" 
                  className="inline-flex items-center text-[#FF7066] font-medium hover:underline"
                >
                  Contact our experts
                  <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                  </svg>
                </a>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Potential Outcomes Section - Fix structure */}
      {service.potentialOutcomes && (
        <section className="py-16 bg-white">
          <div className="container px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
              <div className="bg-gradient-to-br from-[#FF7066]/5 via-white to-secondary/5 rounded-3xl p-8 md:p-12 shadow-xl">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                  className="text-center mb-10"
                >
                  <h2 className="text-3xl md:text-4xl font-bold text-[#FF7066] mb-4">{service.potentialOutcomes.title}</h2>
                  <p className="text-xl text-secondary max-w-3xl mx-auto">
                    {service.potentialOutcomes.description}
                  </p>
                </motion.div>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {service.potentialOutcomes.outcomes.map((outcome, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300"
                    >
                      <div className="flex items-start gap-3">
                        <div className="mt-1 p-1 rounded-full bg-[#FF7066]/10">
                          <svg className="w-5 h-5 text-[#FF7066]" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <p className="text-secondary">{outcome}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Final CTA Section - Fix structure */}
      <section className="py-16 bg-gradient-to-b from-white to-gray-50">
        <div className="container px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="max-w-4xl mx-auto text-center bg-white rounded-2xl p-12 shadow-2xl border border-[#FF7066]/10"
          >
            <h2 className="text-3xl font-bold mb-4">Ready to transform your business with {service.title}?</h2>
            <p className="text-secondary text-lg mb-8 max-w-2xl mx-auto">
              Our team of experts is ready to discuss your specific requirements and demonstrate how our solution can be tailored to your organisation's unique challenges.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="https://calendly.com/amrit-betterbyai/30min" 
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary bg-[#FF7066] text-white px-8 py-4 rounded-lg hover:bg-[#FF8A82] transition-colors shadow-lg shadow-[#FF7066]/20"
              >
                Book a Consultation
              </a>
              <a 
                href="mailto:contact@betterbyai.com" 
                className="btn-secondary border-2 border-[#FF7066] text-[#FF7066] px-8 py-4 rounded-lg hover:bg-[#FF7066]/10 transition-colors"
              >
                Request Information
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Related Services - Fix structure */}
      <section className="py-16 bg-gray-50">
        <div className="container px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-[#FF7066] mb-4">Related Services</h2>
              <p className="text-secondary">Explore other AI solutions that complement {service.title}</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {servicesData
                .filter(s => s.slug !== service.slug)
                .slice(0, 3)
                .map((relatedService, index) => (
                  <Link key={index} to={`/services/${relatedService.slug}`}>
                    <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 hover:-translate-y-1">
                      <div className="p-2 w-fit rounded-lg bg-[#FF7066]/10 mb-4">
                        {relatedService.icon && <relatedService.icon className="w-8 h-8 text-[#FF7066]" />}
                      </div>
                      <h3 className="text-xl font-semibold mb-2 text-[#FF7066]">{relatedService.title}</h3>
                      <p className="text-secondary line-clamp-2 mb-4">{relatedService.description}</p>
                      <div className="text-[#FF7066] font-medium flex items-center">
                        Learn more
                        <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                        </svg>
                      </div>
                    </div>
                  </Link>
                ))
              }
            </div>
          </div>
        </div>
      </section>

      {/* Contact Component */}
      <Contact />
    </div>
  );
}